import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { projectTemplatesService } from '../api/project-templates.service';
import { PROJECT_TEMPLATE_KEYS } from './queryKeys';
import type {
  CreateProjectTemplatePayload,
  UpdateTemplateMetadataPayload,
  UpdateTemplateStructurePayload,
} from '../types';

export function useCreateProjectTemplate() {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (payload: CreateProjectTemplatePayload) => projectTemplatesService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECT_TEMPLATE_KEYS.byOrg(orgId ?? '') });
    },
  });
}

export function useUpdateProjectTemplateMetadata(templateId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (payload: UpdateTemplateMetadataPayload) =>
      projectTemplatesService.updateMetadata(templateId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECT_TEMPLATE_KEYS.byOrg(orgId ?? '') });
    },
  });
}

export function useUpdateProjectTemplateStructure(templateId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (payload: UpdateTemplateStructurePayload) =>
      projectTemplatesService.updateStructure(templateId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PROJECT_TEMPLATE_KEYS.detail(orgId ?? '', templateId),
      });
      queryClient.invalidateQueries({ queryKey: PROJECT_TEMPLATE_KEYS.byOrg(orgId ?? '') });
    },
  });
}

export function useDeleteProjectTemplate() {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (templateId: string) => projectTemplatesService.delete(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECT_TEMPLATE_KEYS.byOrg(orgId ?? '') });
    },
  });
}
